import { FC, useEffect, useState, useMemo } from "react"
import { handleAuthorizedRequest, spotify, trends } from "../../../../apis"
import { KSpan } from "kahuna-base-react-components"
import {
  formatDate,
  formatNumber,
  getPercentage,
  mapToCamelCase,
  processPlatformOptions,
  toYYYYMMDD,
  withAlpha
} from "../../../../utility"
import { TrendTrack, User } from "../../../../types"
//@ts-ignore
import HeadphonesIcon from "../../../../assets/analytics_icons/headphones-black.svg"
//@ts-ignore
import SkipRightIcon from "../../../../assets/analytics_icons/skip.svg"
//@ts-ignore
import BookmarkIcon from "../../../../assets/analytics_icons/bookmark-fill.svg"
//@ts-ignore
import MusicNote from "../../../../assets/analytics_icons/music-note-black.svg"
//@ts-ignore
import DownloadIcon from "../../../../assets/analytics_icons/download.svg"
//@ts-ignore
import ViewIcon from "../../../../assets/analytics_icons/eye.svg"
//@ts-ignore
import VideoReelsIcon from "../../../../assets/analytics_icons/video-reels.svg"

import { lang } from "../../../../constants/languages"
import { RootState } from "../../../../store"
import { connect } from "react-redux"
import {
  colorPaletteOptions,
  DAY,
  defaultEndDate,
  defaultStartDate,
  platformColorPalette,
  TREND_TRACK_EVENT_TYPE
} from "../../../../constants"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRangeDate/KSelectRangeDate"
import { DailyPerformanceChart } from "./DailyPerformanceChart"
import Card from "../components/Card"
import { capitalize } from "lodash"
import NotFound from "../../../NotFound"
import Loading from "../../../ui/Loading"

export interface PlatformPerformanceDetails {
  dsp: number
  download: number
  socialProduction: number
  socialConsumption: number
  skips: number
  saves: number
}
export interface PlatformPerformanceData {
  platformMap: {
    [platform: string]: {
      [date: string]: {
        [metric: string]: number
      }
    }
  }
  totalMap: { [key: string]: { previousPeriod: PlatformPerformanceDetails; selectedPeriod: PlatformPerformanceDetails } }
  platformList: string[]
}

export type PlatformPerformanceProps = {
  user: User
  dateValues: DateRangeType
}

export const platformMetricMap = {
  apple: ["dsp", "saves"],
  audiomack: ["dsp", "download"],
  boomplay: ["dsp"],
  deezer: ["dsp", "skips"],
  facebook: ["socialProduction", "socialConsumption"], // social_production, social_consumption
  instagram: ["socialProduction", "socialConsumption"], // social_production, social_consumption
  itunes: ["download"],
  shazam: ["dsp"], 
  snap: ["socialProduction", "socialConsumption"], // social_production, social_consumption
  soundcloud: ["dsp"],
  spotify: ["dsp", "skips", "saves"]
}

const PlatformPerformance: FC<PlatformPerformanceProps> = (props) => {
  const {dateValues} = props
  const [platformPerformanceData, setPlatformPerformanceData] = useState<PlatformPerformanceData>()
  const [selectedPlatform, setSelectedPlatform] = useState<string>("")
  const [platformOptions, setPlatformOptions] = useState<KSelectOption[]>([])
  const [trendTrackData, setTrendTrackData] = useState<{ [key: string]: { [key: string]: number } }>({})
  const [details, setDetails] = useState<{
    previousPeriod: PlatformPerformanceDetails
    selectedPeriod: PlatformPerformanceDetails
  }>({
    previousPeriod: { dsp: 0, download: 0, socialProduction: 0, socialConsumption: 0, saves: 0, skips: 0 },
    selectedPeriod: { dsp: 0, download: 0, socialProduction: 0, socialConsumption: 0, saves: 0, skips: 0 }
  })
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [loading, setLoading] = useState(false)

  const fetchPlatformPerformance = async (startDate: Date, endDate: Date) => {
    const start_date = toYYYYMMDD(startDate)
    const end_date = toYYYYMMDD(endDate)
    setLoading(true)
    try {
      await handleAuthorizedRequest(async () => {
        const response = await trends(`/platform_performance/?start_date=${start_date}&end_date=${end_date}`, "get")
        if (response && response.data) {
          const platformData: PlatformPerformanceData = mapToCamelCase(response.data)
          setPlatformPerformanceData(platformData)
          const platformOptions = (platformData?.platformList || [])?.sort((a, b) => a.localeCompare(b))
          const processedPlatformOptions = processPlatformOptions(platformOptions)
          setPlatformOptions(processedPlatformOptions)
          const initialPlatform = processedPlatformOptions?.[0]?.value2 || ""
          setSelectedPlatform(initialPlatform)
        }
      }, "")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const dates = dateValues as Date[]
    if (!(dates[0] && dates[1])) return
    //const start = new Date(dates[0].getTime() - 7 * DAY) // It is necessary to get the previous week data
    const startDate = dates[0]
    const endDate = dates[1]
    const range = endDate.getTime() - startDate.getTime()
    const start = new Date(startDate.getTime() - (range + DAY))
    fetchPlatformPerformance(start, dates[1])
  }, [dateValues])

  useEffect(() => {
    if (platformPerformanceData?.platformMap && selectedPlatform) {
      const directData = platformPerformanceData.platformMap[selectedPlatform] || {}
      setTrendTrackData(directData)
    }

    const totalMap = platformPerformanceData?.totalMap?.[selectedPlatform]
    if (totalMap && totalMap.previousPeriod && totalMap.selectedPeriod) {
      setDetails({
        previousPeriod: totalMap.previousPeriod,
        selectedPeriod: totalMap.selectedPeriod
      })
    }
  }, [selectedPlatform, platformPerformanceData])

  const getChartTitle = (metricKey: string) => {
    if (metricKey === "dsp") {
      return lang.trends.headers.daily_streams
    } else if (metricKey === "download") {
      return lang.trends.headers.daily_downloads
    } else if (metricKey === "socialProduction") {
      return lang.trends.headers.daily_videos_created
    } else if (metricKey === "socialConsumption") {
      return lang.trends.headers.daily_video_views
    }
    return lang.trends.headers.daily_streams
  }

  const getCardIcon = (metricKey: string) => {
    if (metricKey === "dsp") {
      return HeadphonesIcon
    } else if (metricKey === "download") {
      return DownloadIcon
    } else if (metricKey === "socialProduction") {
      return VideoReelsIcon
    } else if (metricKey === "socialConsumption") {
      return ViewIcon
    } else if (metricKey === "skips") {
      return SkipRightIcon
    } else if (metricKey === "saves") {
      return BookmarkIcon
    }
    return HeadphonesIcon
  }

  const getCardTitle = (metricKey: string) => {
    if (metricKey === "dsp") {
      return lang.trends.headers.streams
    } else if (metricKey === "download") {
      return lang.trends.headers.downloads
    } else if (metricKey === "socialProduction") {
      return lang.trends.headers.videos_created
    } else if (metricKey === "socialConsumption") {
      return lang.trends.headers.video_views
    } else if (metricKey === "skips") {
      return lang.trends.skips
    } else if (metricKey === "saves") {
      return lang.trends.saves
    }
    return lang.trends.headers.streams
  }

  const metricKeys = useMemo(() => {
    const keys = platformMetricMap?.[selectedPlatform as keyof typeof platformMetricMap] ?? []
    return keys.filter((metricKey: string) => metricKey !== "skips" && metricKey !== "saves")
  }, [platformMetricMap, selectedPlatform])

  if (loading) return <Loading />

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-row gap-5 my-10 items-center justify-between">
        <div className="flex flex-row items-center gap-3 flex-1 flex-wrap">
          {[...platformOptions]?.map((platformOption, index) => {
            
            const color = (platformColorPalette as any)[platformOption.label] || colorPaletteOptions[index]
            return (
              <div
                key={`${platformOption.label}`}
                className="p-2 cursor-pointer"
                style={{
                  borderRadius: 12,
                  border: "1px solid #F3F3F3",
                  //backgroundColor: selectedPlatforms.includes(platformOption?.value2 || "") ? "#FFF" : "#F3F3F3",
                  boxShadow: selectedPlatform.includes(platformOption?.value2 || "")
                    ? `0px 1px 3px 1px ${withAlpha(color, 0.8)}, 
                        0px -1px 3px 1px ${withAlpha(color, 0.8)}`
                    : "0px 2px 4px 0px rgba(27, 28, 29, 0.04)",
                  transform: selectedPlatform.includes(platformOption?.value2 || "") ? "scale(1.05)" : ""
                }}
                onClick={() => {
                  setSelectedPlatform(platformOption.value2 || "")
                  setSelectedIndex(index)
                }}
              >
                {platformOption.iconLabel ? (
                  <img src={platformOption.iconLabel} width={80} />
                ) : (
                  <div className="flex flex-row gap-2 items-center">
                    {platformOption.icon && <img src={platformOption.icon} width={24} height={24} />}
                    <KSpan text={platformOption.label} color="#111" lineHeight="24px" />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
      <div className="flex gap-1 max-[1200px]:flex-col">
        {selectedPlatform && metricKeys.length && metricKeys.length > 0 ? (
          metricKeys.map((metricKey: string) => {
            return (
              <div key={`${selectedPlatform}-${metricKey}`} className="min-h-0 flex-1 h-[400px]">
                <DailyPerformanceChart
                  data={trendTrackData}
                  selectedIndex={selectedIndex}
                  dateValues={dateValues}
                  platform={selectedPlatform}
                  dataKey={metricKey}
                  title={`${getChartTitle(metricKey)}`}
                />
              </div>
            )
          })
        ) : (
          <NotFound title={lang.not_found.trends.title} description={lang.not_found.trends.desc} imageWidth={400} />
        )}
      </div>
      <div className="flex gap-3 w-full max-[1200px]:!block max-[1200px]:w-full">
        {selectedPlatform &&
          [
            ...new Set([
              ...(platformMetricMap?.[selectedPlatform as keyof typeof platformMetricMap] || []),
              "skips",
              "saves"
            ])
          ].map((metricKey: string) => {
            const key = metricKey as keyof PlatformPerformanceDetails
            const originalList = platformMetricMap?.[selectedPlatform as keyof typeof platformMetricMap]
            const dataNotAvailable = ["skips", "saves"]?.includes(key) && !originalList?.includes(key)
            const percentChange = getPercentage(details?.previousPeriod?.[key], details?.selectedPeriod?.[key])
            return (
              <div
                key={`${selectedPlatform}-${key}`}
                className="flex min-h-0 flex-1 max-[1200px]:mb-3"
                style={{
                  minWidth: 180,
                  flex: dataNotAvailable ? 1 : 2,
                  flexShrink: dataNotAvailable ? 0 : 1,
                  flexBasis: 0
                }}
              >
                <Card
                  value={dataNotAvailable ? "0.0" : formatNumber(details?.selectedPeriod?.[key] || 0)}
                  title={getCardTitle(key)}
                  icon={getCardIcon(key)}
                  percentChange={percentChange}
                  tooltipContent={
                    dataNotAvailable
                      ? lang.trends.no_data_tooltip.replace("<param>", `"${getCardTitle(key)}"`)
                      : undefined
                  }
                  disabled={dataNotAvailable}
                />
              </div>
            )
          })}
      </div>
      {/*<div className="flex h-[400px] gap-3 max-[1000px]:flex-col max-[1000px]:!h-[800px]">
        <div
          style={{
            borderRadius: "10px",
            border: "1px solid var(--stroke-soft-200, #F3F3F3)",
            background: " var(--bg-white-0, #FFF)",
            boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
          }}
          className="flex min-w-0 basis-0 flex-1 h-full items-center justify-center"
        >
          <KSpan text="Source Breakdown" color="#111"/>
        </div>
      </div>*/}
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps)(PlatformPerformance)
